// Extracted from EnhancedResults.js (structural refactor, July 2026).
// Analysis-result view components — no behavior change.
import React, { useState } from 'react';

export function HeritabilityView({ data }) {
  if (!data) return null;
  const vc = data.variance_components || [];
  const ht = data.heritability_table || [];
  const ga = data.genetic_advance_table || [];
  const gp = data.gcv_pcv_table || [];

  const fmtP = (p) => {
    if (p === null || p === undefined) return '-';
    if (typeof p === 'string') return p;
    if (p < 0.001) return '<0.001';
    return p.toFixed(4);
  };

  const classColor = (cls) => {
    if (cls === 'High') return 'text-red-600 font-bold';
    if (cls === 'Moderate') return 'text-amber-600 font-semibold';
    return 'text-green-700';
  };

  const thCls = 'px-3 py-2 text-center font-semibold border border-[#0E7490]/30';
  const tdCls = 'px-3 py-1.5 text-center font-mono border border-[#E8E6E1]';
  const tdLCls = 'px-3 py-1.5 font-semibold border border-[#E8E6E1]';

  return (
    <div className="space-y-8" data-testid="heritability-view">
      {data.interpretation && (
        <div className="bg-[#EAF0EC] rounded-2xl p-5 border border-[#0E7490]/20">
          <p className="text-sm text-[#2C2A28] leading-relaxed whitespace-pre-line">{data.interpretation}</p>
        </div>
      )}

      {data.note && (
        <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
          <p className="text-xs text-amber-800 italic">{data.note}</p>
        </div>
      )}

      {/* 1. Variance Components Table */}
      {vc.length > 0 && (
        <div data-testid="herit-variance-components">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Variance Components (ANOVA Method)</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#0E7490] text-white">
                {['Trait', 'VG', 'VE', 'VP', 'VGxE', 'F-value', 'P-value'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {vc.map((row, i) => (
                  <tr key={row.Trait} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0F9FA]'}>
                    <td className={tdLCls}>{row.Trait}</td>
                    <td className={tdCls}>{row.VG.toFixed(6)}</td>
                    <td className={tdCls}>{row.VE.toFixed(6)}</td>
                    <td className={tdCls}>{row.VP.toFixed(6)}</td>
                    <td className={`${tdCls} text-gray-400 italic`}>{row.VGxE}</td>
                    <td className={tdCls}>{row.F}</td>
                    <td className={tdCls}>{fmtP(row.P)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 2. Heritability Table */}
      {ht.length > 0 && (
        <div data-testid="herit-heritability-table">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Heritability Estimates</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#0E7490] text-white">
                {['Trait', 'h\u00B2 (Broad-Sense) %', 'SE', 'h\u00B2 (Narrow-Sense) %', 'SE'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {ht.map((row, i) => {
                  const h2Cat = row.h2_broad > 60 ? 'High' : (row.h2_broad > 30 ? 'Moderate' : 'Low');
                  return (
                    <tr key={row.Trait} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0F9FA]'}>
                      <td className={tdLCls}>{row.Trait}</td>
                      <td className={`${tdCls} ${row.h2_broad > 60 ? 'bg-emerald-100 font-bold' : row.h2_broad > 30 ? 'bg-amber-50' : 'bg-red-50'}`}>
                        {row.h2_broad.toFixed(2)}
                        <span className="text-[10px] ml-1 opacity-60">({h2Cat})</span>
                      </td>
                      <td className={`${tdCls} text-gray-500`}>{'\u00B1'}{row.SE_h2_broad.toFixed(2)}</td>
                      <td className={tdCls}>{row.h2_narrow.toFixed(2)}</td>
                      <td className={`${tdCls} text-gray-500`}>{'\u00B1'}{row.SE_h2_narrow.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 3. Genetic Advance Table */}
      {ga.length > 0 && (
        <div data-testid="herit-genetic-advance">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Genetic Advance (5% Selection Intensity, k=2.06)</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#0E7490] text-white">
                {['Trait', 'GA', 'GA as % of Mean', 'Grand Mean'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {ga.map((row, i) => {
                  const gaCat = row.GA_percent_mean > 20 ? 'High' : (row.GA_percent_mean > 10 ? 'Moderate' : 'Low');
                  return (
                    <tr key={row.Trait} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0F9FA]'}>
                      <td className={tdLCls}>{row.Trait}</td>
                      <td className={tdCls}>{row.GA.toFixed(4)}</td>
                      <td className={`${tdCls} ${row.GA_percent_mean > 20 ? 'bg-emerald-100 font-bold' : row.GA_percent_mean > 10 ? 'bg-amber-50' : ''}`}>
                        {row.GA_percent_mean.toFixed(2)}%
                        <span className="text-[10px] ml-1 opacity-60">({gaCat})</span>
                      </td>
                      <td className={tdCls}>{row.Grand_Mean.toFixed(4)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 4. GCV and PCV Table */}
      {gp.length > 0 && (
        <div data-testid="herit-gcv-pcv">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">GCV & PCV Classification</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#0E7490] text-white">
                {['Trait', 'GCV (%)', 'GCV Class', 'PCV (%)', 'PCV Class'].map((h, idx) => (
                  <th key={`${h}-${idx}`} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {gp.map((row, i) => (
                  <tr key={row.Trait} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0F9FA]'}>
                    <td className={tdLCls}>{row.Trait}</td>
                    <td className={tdCls}>{row.GCV.toFixed(2)}</td>
                    <td className={`${tdCls} ${classColor(row.GCV_class)}`}>{row.GCV_class}</td>
                    <td className={tdCls}>{row.PCV.toFixed(2)}</td>
                    <td className={`${tdCls} ${classColor(row.PCV_class)}`}>{row.PCV_class}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex gap-4 text-[10px] text-[#8A8580]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-600 inline-block" /> Low (&lt;10%)</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> Moderate (10–20%)</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> High (&gt;20%)</span>
          </div>
        </div>
      )}

      {/* Charts */}
      {data.charts && data.charts.length > 0 && (
        <div className="space-y-6" data-testid="herit-charts">
          {data.charts.map((c, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#E8E6E1] p-4 shadow-sm" data-testid={`herit-chart-${i}`}>
              <img src={`data:image/png;base64,${c}`} alt={['Heritability Bar Chart', 'GCV vs PCV Chart', 'h² vs GA% Quadrant Plot'][i] || `Chart ${i+1}`} className="w-full max-w-[800px] mx-auto rounded-lg" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ============================================================
   SELECTION INDEX VIEW
============================================================ */


export function SelectionIndexView({ data }) {
  const [activeMethod, setActiveMethod] = useState('Smith-Hazel');
  if (!data) return null;

  const coeff = data.coefficients_table || [];
  const scores = data.index_scores || {};
  const ggTable = data.genetic_gain_table || [];
  const effTable = data.efficiency_table || [];
  const methodNames = data.method_names || Object.keys(scores);
  const charts = data.charts || [];
  const pvals = data.trait_pvalues || {};

  const fmtP = (p) => {
    if (p === null || p === undefined) return '-';
    if (typeof p === 'string') return p;
    if (p < 0.001) return '<0.001';
    return p.toFixed(4);
  };

  const thCls = 'px-3 py-2 text-center font-semibold border border-[#7C3AED]/30';
  const tdCls = 'px-3 py-1.5 text-center font-mono border border-[#E8E6E1]';
  const tdLCls = 'px-3 py-1.5 font-semibold border border-[#E8E6E1]';

  return (
    <div className="space-y-8" data-testid="selection-index-view">
      {data.interpretation && (
        <div className="bg-[#F3EEFA] rounded-2xl p-5 border border-[#7C3AED]/20">
          <p className="text-sm text-[#2C2A28] leading-relaxed whitespace-pre-line">{data.interpretation}</p>
        </div>
      )}

      {/* 1. Coefficients Table */}
      {coeff.length > 0 && (
        <div data-testid="si-coefficients-table">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Selection Index Coefficients</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#7C3AED] text-white">
                <th className={thCls}>Trait</th>
                {methodNames.map(m => <th key={m} className={thCls}>{m}</th>)}
                <th className={thCls}>P-value</th>
              </tr></thead>
              <tbody>
                {coeff.map((row, i) => (
                  <tr key={row.Trait} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F8F5FF]'}>
                    <td className={tdLCls}>{row.Trait}</td>
                    {methodNames.map(m => (
                      <td key={m} className={`${tdCls} ${row[m] > 0 ? 'text-emerald-700' : row[m] < 0 ? 'text-red-600' : ''}`}>
                        {typeof row[m] === 'number' ? row[m].toFixed(4) : row[m]}
                      </td>
                    ))}
                    <td className={tdCls}>{fmtP(pvals[row.Trait])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 2. Index Scores Table (tabbed by method) */}
      {Object.keys(scores).length > 0 && (
        <div data-testid="si-scores-table">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Genotype Rankings by Index Score</h4>
          <div className="flex gap-2 mb-3 flex-wrap">
            {methodNames.map(m => (
              <button
                key={m}
                onClick={() => setActiveMethod(m)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                  activeMethod === m
                    ? 'bg-[#7C3AED] text-white border-[#7C3AED]'
                    : 'bg-white text-[#5E5A56] border-[#E8E6E1] hover:border-[#7C3AED]/40'
                }`}
                data-testid={`si-method-btn-${m.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {m}
              </button>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#7C3AED] text-white">
                {['Rank', 'Genotype', 'Index Score'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(scores[activeMethod] || []).map((row, i) => {
                  const isTop = i < (data.n_top_selected || 10);
                  return (
                    <tr key={row.Genotype} className={isTop ? 'bg-emerald-50' : (i % 2 === 0 ? 'bg-white' : 'bg-[#F8F5FF]')}>
                      <td className={`${tdCls} ${isTop ? 'font-bold text-emerald-700' : ''}`}>{row.Rank}</td>
                      <td className={`${tdLCls} ${isTop ? 'text-emerald-700' : ''}`}>{row.Genotype}</td>
                      <td className={`${tdCls} ${isTop ? 'font-bold text-emerald-700' : ''}`}>{row.Score.toFixed(4)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 3. Genetic Gain Table */}
      {ggTable.length > 0 && (
        <div data-testid="si-genetic-gain-table">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Expected Genetic Gain (5% Selection Intensity)</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#7C3AED] text-white">
                {['Trait', 'Single-Trait Gain', 'Index Gain (Smith-Hazel)'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {ggTable.map((row, i) => (
                  <tr key={row.Trait} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F8F5FF]'}>
                    <td className={tdLCls}>{row.Trait}</td>
                    <td className={tdCls}>{row.Single_Trait_Gain.toFixed(4)}</td>
                    <td className={`${tdCls} ${row.Index_Gain >= row.Single_Trait_Gain ? 'text-emerald-700 font-bold' : 'text-red-600'}`}>
                      {row.Index_Gain.toFixed(4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 4. Efficiency Table */}
      {effTable.length > 0 && (
        <div data-testid="si-efficiency-table">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Efficiency of Index over Single-Trait Selection</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#7C3AED] text-white">
                {['Trait', 'Single-Trait Gain', 'Index Gain', 'Efficiency (%)'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {effTable.map((row, i) => (
                  <tr key={row.Trait} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F8F5FF]'}>
                    <td className={tdLCls}>{row.Trait}</td>
                    <td className={tdCls}>{row.Single_Trait_Gain.toFixed(4)}</td>
                    <td className={tdCls}>{row.Index_Gain.toFixed(4)}</td>
                    <td className={`${tdCls} font-bold ${row.Efficiency_pct >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      {row.Efficiency_pct >= 0 ? '+' : ''}{row.Efficiency_pct.toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Charts */}
      {charts.length > 0 && (
        <div className="space-y-6" data-testid="si-charts">
          {charts.map((c, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#E8E6E1] p-4 shadow-sm" data-testid={`si-chart-${i}`}>
              <img src={`data:image/png;base64,${c}`} alt={['Index Score Rankings', 'Genetic Gain Comparison'][i] || `Chart ${i+1}`} className="w-full max-w-[800px] mx-auto rounded-lg" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ============================================================
   NON-PARAMETRIC TESTS VIEW
============================================================ */
